import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { ResponsiveStyleValue } from '@mui/system'
import React, { useState } from 'react'
import {
  ComponentSourceType,
  ComponentType,
  CompositeActualValue,
  CompositeFunction,
  CompositeType,
  evaluateCompositeMetricValue,
  parseCompositeActualValue,
  parseCompositeTargetValue,
} from 'types/components'

const getTypeDisplayName = (type: ComponentType) => {
  switch (type) {
    case ComponentType.COMPONENT_TYPE_INTEGER:
      return 'Numeric'
    case ComponentType.COMPONENT_TYPE_CURRENCY:
      return 'Currency'
    case ComponentType.COMPONENT_TYPE_PERCENTAGE:
      return 'Percentage'
    case ComponentType.COMPONENT_TYPE_BOOL:
      return 'Boolean'
    case ComponentType.COMPONENT_TYPE_FLOAT:
      return 'Numeric'
    default:
      return type
  }
}

interface ComponentDisplayProps {
  componentType: ComponentType
  value: string | number | boolean | null
  currency?: string
  currencyDecimals?: number
  decimalPlaces?: number
  numericDecimalPlaces?: number
  typographyComponent?: React.ElementType
  fontSize?: ResponsiveStyleValue<string | number | undefined>
  fontWeight?: 'normal' | 'bold' | 'light' | 'medium' | number
  color?: 'inherit' | 'primary' | 'secondary' | 'textPrimary' | 'textSecondary' | 'error' | string
  sourceType?: ComponentSourceType
  compositeFunction?: CompositeFunction
  compositeType?: CompositeType
  lowerIsBetter?: boolean
  componentLabel?: string
  periodLabel?: string
  isTarget?: boolean
}

const ComponentDisplay: React.FC<ComponentDisplayProps> = ({
  componentType,
  value,
  currency = 'USD',
  currencyDecimals = 0,
  decimalPlaces = 2,
  numericDecimalPlaces,
  typographyComponent = 'span',
  fontWeight = 'normal',
  color = 'inherit',
  sourceType = ComponentSourceType.COMPONENT_SOURCE_MANUAL,
  compositeFunction = CompositeFunction.COMPOSITE_FUNCTION_NONE,
  compositeType = CompositeType.COMPOSITE_TYPE_SIMPLE,
  lowerIsBetter = false,
  componentLabel = 'Component',
  periodLabel,
  isTarget = false,
  fontSize,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [compositeData, setCompositeData] = useState<CompositeActualValue | null>(null)
  const theme = useTheme()

  const handleOpen = () => {
    if (sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE && typeof value === 'string') {
      if (isTarget) {
        const parsedTargetValue = parseCompositeTargetValue(value)
        const convertedData: CompositeActualValue = {
          values: (parsedTargetValue?.compositeTargetValues || []).map((item) => ({
            label: item.label,
            description: item.description,
            value: item.target,
            componentType: item.componentType,
          })),
        }
        setCompositeData(convertedData)
      } else {
        setCompositeData(parseCompositeActualValue(value))
      }
    }
    setDialogOpen(true)
  }

  const handleClose = () => setDialogOpen(false)

  const getFormattedValue = (inputValue: any, overrideType?: ComponentType, overrideDecimals?: number) => {
    const typeToUse = overrideType || componentType
    // Use numericDecimalPlaces if available, otherwise fall back to overrideDecimals or decimalPlaces
    const effectiveDecimals =
      numericDecimalPlaces !== undefined
        ? numericDecimalPlaces
        : overrideDecimals !== undefined
          ? overrideDecimals
          : decimalPlaces

    switch (typeToUse) {
      case ComponentType.COMPONENT_TYPE_CURRENCY:
        const currencyValue = Number(inputValue)
        const currencyDecimalPlaces =
          numericDecimalPlaces !== undefined
            ? numericDecimalPlaces
            : overrideDecimals !== undefined
              ? overrideDecimals
              : currencyDecimals

        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
          minimumFractionDigits: currencyDecimalPlaces,
          maximumFractionDigits: currencyDecimalPlaces,
        }).format(currencyValue)
      case ComponentType.COMPONENT_TYPE_INTEGER:
        return new Intl.NumberFormat('en-US', {
          style: 'decimal',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(Number(inputValue))
      case ComponentType.COMPONENT_TYPE_FLOAT:
        const floatValue = Number(inputValue)

        return new Intl.NumberFormat('en-US', {
          style: 'decimal',
          minimumFractionDigits: effectiveDecimals,
          maximumFractionDigits: effectiveDecimals,
        }).format(floatValue)
      case ComponentType.COMPONENT_TYPE_PERCENTAGE:
        const percentValue = Number(inputValue)

        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: effectiveDecimals,
          maximumFractionDigits: effectiveDecimals,
        }).format(percentValue)
      case ComponentType.COMPONENT_TYPE_BOOL:
        // Handle both boolean and string boolean values with multiple variations
        if (typeof inputValue === 'string') {
          const lowercaseValue = inputValue.toLowerCase()
          const truthyValues = ['true', '1', 't', 'y', 'yes']
          return truthyValues.includes(lowercaseValue) ? 'True' : 'False'
        }
        return inputValue ? 'True' : 'False'
      default:
        return inputValue?.toString() ?? ''
    }
  }

  const getCompositeTargetDisplayValue = (inputValue: string) => {
    if (!inputValue) return ''

    const parsedTarget = parseCompositeTargetValue(inputValue)
    const overallValue = parsedTarget?.value ? Number(parsedTarget.value) : 0

    return `${getFormattedValue(Number.isFinite(overallValue) ? overallValue : 0, componentType)}*`
  }

  const getCompositeDisplayValue = (inputValue: string) => {
    if (!inputValue) return ''

    const compositeReduction = evaluateCompositeMetricValue(inputValue, {
      compositeFunction,
      compositeType,
      lowerIsBetter,
      fallbackComponentType: componentType,
    })

    const reducedValue = compositeReduction.reducedValue
    const useDecimalPlaces =
      compositeReduction.functionUsed === CompositeFunction.COMPOSITE_FUNCTION_AVERAGE ||
      compositeReduction.functionUsed === CompositeFunction.COMPOSITE_FUNCTION_STDDEV

    if (useDecimalPlaces) {
      if (compositeType === CompositeType.COMPOSITE_TYPE_COMPLEX) {
        return `${getFormattedValue(reducedValue, ComponentType.COMPONENT_TYPE_PERCENTAGE)}*`
      }

      if (componentType === ComponentType.COMPONENT_TYPE_CURRENCY) {
        const decimalsToUse = numericDecimalPlaces !== undefined ? numericDecimalPlaces : decimalPlaces
        return `${getFormattedValue(reducedValue, undefined, decimalsToUse)}*`
      }

      if (componentType === ComponentType.COMPONENT_TYPE_INTEGER) {
        return `${getFormattedValue(reducedValue, ComponentType.COMPONENT_TYPE_FLOAT)}*`
      }

      return `${getFormattedValue(reducedValue)}*`
    }

    return `${getFormattedValue(reducedValue)}*`
  }

  const displayValue =
    sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE && typeof value === 'string'
      ? isTarget
        ? getCompositeTargetDisplayValue(value)
        : getCompositeDisplayValue(value)
      : getFormattedValue(value)

  return (
    <>
      <Typography
        variant="body1"
        component={typographyComponent}
        fontWeight={fontWeight}
        color={color}
        fontSize={fontSize}
        sx={
          sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
            ? {
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                },
              }
            : {}
        }
        onClick={sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE ? handleOpen : undefined}
      >
        {displayValue}
      </Typography>

      {sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE && (
        <Dialog
          open={dialogOpen}
          onClose={handleClose}
          aria-labelledby="composite-details-dialog"
          maxWidth="md"
          fullWidth={true}
        >
          <DialogTitle
            id="composite-details-dialog"
            sx={{
              m: 0,
              p: 2,
              color: (theme) => theme.palette.primary.main,
            }}
          >
            {periodLabel ? `${componentLabel} - ${periodLabel}` : componentLabel}
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ minWidth: '600px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                {isTarget ? `Composite Function: ${compositeFunction}` : `Composite Function: ${compositeFunction}`}
              </Typography>
              {compositeData && (
                <Typography variant="subtitle1" fontWeight="medium" sx={{ mr: 3 }}>
                  {isTarget ? 'Target Value:' : 'Applied Value:'} {displayValue.replace('*', '')}
                </Typography>
              )}
            </Box>

            {compositeData &&
              (compositeData.values.length > 0 ? (
                <TableContainer sx={{ mt: 2 }}>
                  <Table stickyHeader size="small" aria-label="composite values table">
                    <TableHead
                      sx={{
                        '& th': {
                          borderTop: `1px solid ${theme.palette.divider}`,
                          borderBottom: `2px solid ${theme.palette.divider} !important`,
                        },
                      }}
                    >
                      <TableRow>
                        <TableCell
                          sx={{
                            minWidth: 120,
                            position: 'sticky !important',
                            overflow: 'hidden',
                          }}
                        >
                          Label
                        </TableCell>
                        <TableCell
                          sx={{
                            minWidth: 250,
                            position: 'sticky !important',
                            overflow: 'hidden',
                          }}
                        >
                          Description
                        </TableCell>
                        <TableCell
                          sx={{
                            minWidth: 60,
                            position: 'sticky !important',
                            overflow: 'hidden',
                          }}
                        >
                          Type
                        </TableCell>
                        {!isTarget && (
                          <TableCell
                            align="right"
                            sx={{
                              minWidth: 100,
                              position: 'sticky !important',
                              overflow: 'hidden',
                            }}
                          >
                            Target
                          </TableCell>
                        )}
                        <TableCell
                          align="right"
                          sx={{
                            minWidth: 100,
                            position: 'sticky !important',
                            overflow: 'hidden',
                          }}
                        >
                          {isTarget ? 'Target Value' : 'Value'}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {compositeData.values.map((item, index) => (
                        <TableRow key={index} sx={{ py: 3 }} role="row" tabIndex={-1}>
                          <TableCell
                            sx={{
                              overflow: 'hidden',
                            }}
                          >
                            {item.label}
                          </TableCell>
                          <TableCell
                            sx={{
                              overflow: 'hidden',
                            }}
                          >
                            {item.description}
                          </TableCell>
                          <TableCell
                            sx={{
                              overflow: 'hidden',
                            }}
                          >
                            {getTypeDisplayName(item.componentType || componentType)}
                          </TableCell>
                          {!isTarget && (
                            <TableCell
                              align="right"
                              sx={{
                                overflow: 'hidden',
                              }}
                            >
                              {item.target ? getFormattedValue(item.target, item.componentType || componentType) : '-'}
                            </TableCell>
                          )}
                          <TableCell
                            align="right"
                            sx={{
                              overflow: 'hidden',
                            }}
                          >
                            {getFormattedValue(item.value, item.componentType || componentType)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    No composite values defined for this target.
                  </Typography>
                </Box>
              ))}
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default ComponentDisplay
